/**
 * One trading day of a symbol's price history, as `GET {kalaApiPath}/DailyHistory`
 * reports it.
 *
 * Every price here is a rial amount and `tradedQuantity` a share count, so all of them
 * are whole numbers — the endpoint does not report fractions.
 */
export interface DailyHistory {
	/** The trading day, Gregorian and ISO — `2026-09-14T00:00:00`. Sorts correctly as a string. */
	timeOfEvent: string;
	/** حجم — the shares dealt that day. */
	tradedQuantity: number;
	/** اولین — the day's opening trade. */
	openPrice: number;
	/** کمترین — the day's lowest trade. */
	minPrice: number;
	/** بیشترین — the day's highest trade. */
	maxPrice: number;
	/** پایانی — the day's closing price. */
	closePrice: number;
	/** آخرین — the day's settlement price. */
	settlementPrice: number;
	/** The same day as `1405/06/23`. The card prints this rather than converting the Gregorian field. */
	timeOfEventShamsi: string;
}

/** One page of the feed: `items` is the page itself and `totalCount` the whole run. */
export interface DailyHistoryListData {
	items: DailyHistory[];
	totalCount: number;
}

/** One day of the feed, with the closing price its change is measured against. */
export interface DailyHistoryDay {
	day: DailyHistory;
	previousClosePrice: number | null;
}

/**
 * Pairs each day with the closing price of the trading day before it, so a card can
 * print that day's change.
 *
 * The feed's own order is kept — the list renders the days as the endpoint sent them —
 * and the predecessor is found by date instead. That read is what makes the pairing
 * independent of whether the endpoint answers newest-first or oldest-first, which the
 * response does not state.
 *
 * It is asked of whatever days have been loaded so far, so the oldest one comes back with
 * `null` — there is no earlier day to compare against yet, and inventing one would put a
 * change on the card that the feed does not support. It loses that `null` when the next
 * page brings its predecessor in.
 */
export function withPreviousClose(history: readonly DailyHistory[]): DailyHistoryDay[] {
	const oldestFirst = [...history].sort((a, b) => a.timeOfEvent.localeCompare(b.timeOfEvent));

	const previousCloseByDate = new Map<string, number | null>();
	oldestFirst.forEach((day, index) => {
		previousCloseByDate.set(day.timeOfEvent, oldestFirst[index - 1]?.closePrice ?? null);
	});

	return history.map(day => ({ day, previousClosePrice: previousCloseByDate.get(day.timeOfEvent) ?? null }));
}
